import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Globe, Megaphone, ArrowRight } from 'lucide-react';
import { Button, Badge, Table, Modal, Input, Select } from '@/components/ui';
import { policyService } from '@/services/policyService';
import { campaignService } from '@/services/campaignService';
import { procedureTypeService } from '@/services/procedureTypeService';
import type { Policy, PolicyType, CreatePolicyRequest, UpdatePolicyRequest } from '@/types/policy';
import type { Campaign } from '@/types/campaign';
import type { ProcedureType } from '@/types/scheduling';
import styles from './PoliciesPage.module.css';

type BadgeVariant = 'neutral' | 'info' | 'success' | 'warning';

const POLICY_TYPE_LABELS: Record<PolicyType, string> = {
  RESCHEDULE_WINDOW: 'Janela de Reagendamento',
  PREREQUISITE: 'Pré-requisito',
  MIN_INTERVAL: 'Intervalo Mínimo',
  MAX_INTERVAL: 'Intervalo Máximo',
};

const POLICY_TYPE_BADGE: Record<PolicyType, BadgeVariant> = {
  RESCHEDULE_WINDOW: 'info',
  PREREQUISITE: 'warning',
  MIN_INTERVAL: 'neutral',
  MAX_INTERVAL: 'success',
};

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'RESCHEDULE_WINDOW', label: 'Janela de Reagendamento' },
  { value: 'PREREQUISITE', label: 'Pré-requisito' },
  { value: 'MIN_INTERVAL', label: 'Intervalo Mínimo' },
  { value: 'MAX_INTERVAL', label: 'Intervalo Máximo' },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Ativas' },
  { value: 'false', label: 'Inativas' },
];

const POLICY_TYPE_OPTIONS = [
  { value: 'RESCHEDULE_WINDOW', label: 'Janela de Reagendamento' },
  { value: 'PREREQUISITE', label: 'Pré-requisito' },
  { value: 'MIN_INTERVAL', label: 'Intervalo Mínimo' },
  { value: 'MAX_INTERVAL', label: 'Intervalo Máximo' },
];

/**
 * Admin page for managing scheduling policies.
 *
 * Features:
 * - Table listing with filters by campaign, type, and active status
 * - Create/edit policy via modal with dynamic form based on type
 * - Activate/deactivate toggle per policy
 * - Global vs campaign-specific scope indicator
 *
 * Dynamic form fields per policy type:
 * - RESCHEDULE_WINDOW: min days + max days sliders
 * - PREREQUISITE: select procedure A -> before procedure B
 * - MIN_INTERVAL: hours input
 * - MAX_INTERVAL: days input
 */
export function PoliciesPage() {
  const queryClient = useQueryClient();

  // Filter state
  const [typeFilter, setTypeFilter] = useState<PolicyType | ''>('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');
  const [campaignFilter, setCampaignFilter] = useState('');

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPolicyType, setFormPolicyType] = useState<PolicyType>('RESCHEDULE_WINDOW');
  const [formCampaignId, setFormCampaignId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Dynamic parameter state
  const [paramMinDays, setParamMinDays] = useState(1);
  const [paramMaxDays, setParamMaxDays] = useState(7);
  const [paramPrerequisiteFrom, setParamPrerequisiteFrom] = useState('');
  const [paramPrerequisiteTo, setParamPrerequisiteTo] = useState('');
  const [paramMinHours, setParamMinHours] = useState(24);
  const [paramMaxIntervalDays, setParamMaxIntervalDays] = useState(30);

  // Queries
  const queryParams = {
    ...(typeFilter ? { policyType: typeFilter } : {}),
    ...(activeFilter ? { isActive: activeFilter === 'true' } : {}),
    ...(campaignFilter ? { campaignId: campaignFilter } : {}),
  };

  const {
    data: policies = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['policies', queryParams],
    queryFn: () =>
      policyService.getPolicies(
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
      ),
  });

  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignService.getCampaigns(),
  });

  const { data: procedureTypes = [] } = useQuery({
    queryKey: ['procedureTypes'],
    queryFn: () => procedureTypeService.getProcedureTypes(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreatePolicyRequest) => policyService.createPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      closeCreateModal();
    },
    onError: () => {
      setFormError('Erro ao criar regra. Tente novamente.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePolicyRequest }) =>
      policyService.updatePolicy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      closeEditModal();
    },
    onError: () => {
      setFormError('Erro ao atualizar regra. Tente novamente.');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive
        ? policyService.deactivatePolicy(id)
        : policyService.activatePolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });

  // Form helpers
  const buildParametersFromForm = useCallback((): Record<string, string> => {
    switch (formPolicyType) {
      case 'RESCHEDULE_WINDOW':
        return {
          minDays: String(paramMinDays),
          maxDays: String(paramMaxDays),
        };
      case 'PREREQUISITE':
        return {
          prerequisiteProcedureId: paramPrerequisiteFrom,
          dependentProcedureId: paramPrerequisiteTo,
        };
      case 'MIN_INTERVAL':
        return {
          minHours: String(paramMinHours),
        };
      case 'MAX_INTERVAL':
        return {
          maxDays: String(paramMaxIntervalDays),
        };
    }
  }, [
    formPolicyType,
    paramMinDays,
    paramMaxDays,
    paramPrerequisiteFrom,
    paramPrerequisiteTo,
    paramMinHours,
    paramMaxIntervalDays,
  ]);

  const populateFormFromParameters = useCallback(
    (policyType: PolicyType, parameters: Record<string, string>) => {
      switch (policyType) {
        case 'RESCHEDULE_WINDOW':
          setParamMinDays(Number(parameters.minDays) || 1);
          setParamMaxDays(Number(parameters.maxDays) || 7);
          break;
        case 'PREREQUISITE':
          setParamPrerequisiteFrom(parameters.prerequisiteProcedureId ?? '');
          setParamPrerequisiteTo(parameters.dependentProcedureId ?? '');
          break;
        case 'MIN_INTERVAL':
          setParamMinHours(Number(parameters.minHours) || 24);
          break;
        case 'MAX_INTERVAL':
          setParamMaxIntervalDays(Number(parameters.maxDays) || 30);
          break;
      }
    },
    [],
  );

  const resetForm = useCallback(() => {
    setFormName('');
    setFormDescription('');
    setFormPolicyType('RESCHEDULE_WINDOW');
    setFormCampaignId('');
    setFormError(null);
    setParamMinDays(1);
    setParamMaxDays(7);
    setParamPrerequisiteFrom('');
    setParamPrerequisiteTo('');
    setParamMinHours(24);
    setParamMaxIntervalDays(30);
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormError(null);
  }, []);

  const openEditModal = useCallback(
    (policy: Policy) => {
      setFormName(policy.name);
      setFormDescription(policy.description ?? '');
      setFormPolicyType(policy.policyType);
      setFormCampaignId(policy.campaignId ?? '');
      setFormError(null);
      populateFormFromParameters(policy.policyType, policy.parameters);
      setEditingPolicy(policy);
    },
    [populateFormFromParameters],
  );

  const closeEditModal = useCallback(() => {
    setEditingPolicy(null);
    setFormError(null);
  }, []);

  const validateForm = useCallback((): boolean => {
    if (!formName.trim()) {
      setFormError('Nome é obrigatório.');
      return false;
    }

    if (formPolicyType === 'RESCHEDULE_WINDOW') {
      if (paramMinDays >= paramMaxDays) {
        setFormError('Mínimo de dias deve ser menor que máximo de dias.');
        return false;
      }
    }

    if (formPolicyType === 'PREREQUISITE') {
      if (!paramPrerequisiteFrom || !paramPrerequisiteTo) {
        setFormError('Selecione ambos os procedimentos.');
        return false;
      }
      if (paramPrerequisiteFrom === paramPrerequisiteTo) {
        setFormError('Os procedimentos devem ser diferentes.');
        return false;
      }
    }

    if (formPolicyType === 'MIN_INTERVAL' && paramMinHours <= 0) {
      setFormError('Intervalo mínimo deve ser maior que zero.');
      return false;
    }

    if (formPolicyType === 'MAX_INTERVAL' && paramMaxIntervalDays <= 0) {
      setFormError('Intervalo máximo deve ser maior que zero.');
      return false;
    }

    return true;
  }, [
    formName,
    formPolicyType,
    paramMinDays,
    paramMaxDays,
    paramPrerequisiteFrom,
    paramPrerequisiteTo,
    paramMinHours,
    paramMaxIntervalDays,
  ]);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);
      if (!validateForm()) return;

      const payload: CreatePolicyRequest = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        policyType: formPolicyType,
        campaignId: formCampaignId || null,
        parameters: buildParametersFromForm(),
      };
      createMutation.mutate(payload);
    },
    [
      validateForm,
      formName,
      formDescription,
      formPolicyType,
      formCampaignId,
      buildParametersFromForm,
      createMutation,
    ],
  );

  const handleUpdate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!editingPolicy) return;
      setFormError(null);
      if (!validateForm()) return;

      const payload: UpdatePolicyRequest = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        policyType: formPolicyType,
        campaignId: formCampaignId || null,
        isActive: editingPolicy.isActive,
        parameters: buildParametersFromForm(),
      };
      updateMutation.mutate({ id: editingPolicy.id, data: payload });
    },
    [
      editingPolicy,
      validateForm,
      formName,
      formDescription,
      formPolicyType,
      formCampaignId,
      buildParametersFromForm,
      updateMutation,
    ],
  );

  const handleToggleActive = useCallback(
    (policy: Policy) => {
      toggleMutation.mutate({ id: policy.id, isActive: policy.isActive });
    },
    [toggleMutation],
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent, policy: Policy) => {
      event.stopPropagation();
      openEditModal(policy);
    },
    [openEditModal],
  );

  // Helpers
  const getCampaignName = useCallback(
    (campaignId: string | null): string | null => {
      if (!campaignId) return null;
      const campaign = campaigns.find((c: Campaign) => c.id === campaignId);
      return campaign?.name ?? null;
    },
    [campaigns],
  );

  const getProcedureName = useCallback(
    (procedureId: string): string => {
      const procedure = procedureTypes.find((p: ProcedureType) => p.id === procedureId);
      return procedure?.name ?? procedureId;
    },
    [procedureTypes],
  );

  const formatParametersSummary = useCallback(
    (policy: Policy): string => {
      switch (policy.policyType) {
        case 'RESCHEDULE_WINDOW':
          return `${policy.parameters.minDays}-${policy.parameters.maxDays} dias`;
        case 'PREREQUISITE': {
          const from = getProcedureName(policy.parameters.prerequisiteProcedureId ?? '');
          const to = getProcedureName(policy.parameters.dependentProcedureId ?? '');
          return `${from} antes de ${to}`;
        }
        case 'MIN_INTERVAL':
          return `${policy.parameters.minHours}h mínimo`;
        case 'MAX_INTERVAL':
          return `${policy.parameters.maxDays} dias máximo`;
      }
    },
    [getProcedureName],
  );

  const campaignFilterOptions = [
    { value: '', label: 'Todas as campanhas' },
    ...campaigns.map((c: Campaign) => ({ value: c.id, label: c.name })),
  ];

  const campaignSelectOptions = [
    { value: '', label: 'Global (todas as campanhas)' },
    ...campaigns.map((c: Campaign) => ({ value: c.id, label: c.name })),
  ];

  const procedureSelectOptions = procedureTypes.map((p: ProcedureType) => ({
    value: p.id,
    label: p.name,
  }));

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Regra',
      render: (policy: Policy) => (
        <div>
          <span className={styles.policyName}>{policy.name}</span>
          {policy.description && (
            <p className={styles.policyDescription}>{policy.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      width: '180px',
      render: (policy: Policy) => (
        <Badge variant={POLICY_TYPE_BADGE[policy.policyType]}>
          {POLICY_TYPE_LABELS[policy.policyType]}
        </Badge>
      ),
    },
    {
      key: 'parameters',
      header: 'Parâmetros',
      width: '200px',
      render: (policy: Policy) => (
        <span className={styles.policyDescription}>
          {formatParametersSummary(policy)}
        </span>
      ),
    },
    {
      key: 'scope',
      header: 'Escopo',
      width: '160px',
      render: (policy: Policy) => {
        const campaignName = getCampaignName(policy.campaignId);
        return (
          <span className={styles.scopeBadge}>
            <span className={styles.scopeIcon} aria-hidden="true">
              {campaignName ? <Megaphone size={14} /> : <Globe size={14} />}
            </span>
            {campaignName ?? 'Global'}
          </span>
        );
      },
    },
    {
      key: 'active',
      header: 'Ativa',
      width: '80px',
      render: (policy: Policy) => (
        <div className={styles.toggleWrapper}>
          <button
            type="button"
            role="switch"
            aria-checked={policy.isActive}
            aria-label={
              policy.isActive
                ? `Desativar regra ${policy.name}`
                : `Ativar regra ${policy.name}`
            }
            className={[
              styles.toggleButton,
              policy.isActive ? styles.toggleButtonActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={(event) => {
              event.stopPropagation();
              handleToggleActive(policy);
            }}
            disabled={toggleMutation.isPending}
          >
            <span
              className={[
                styles.toggleKnob,
                policy.isActive ? styles.toggleKnobActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden="true"
            />
          </button>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '100px',
      render: (policy: Policy) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleEditClick(e, policy)}
          >
            Editar
          </Button>
        </div>
      ),
    },
  ];

  // Dynamic parameter fields based on policy type
  const renderParameterFields = () => {
    switch (formPolicyType) {
      case 'RESCHEDULE_WINDOW':
        return (
          <div className={styles.parametersSection}>
            <p className={styles.parametersSectionTitle}>Parâmetros da Janela</p>
            <div className={styles.sliderGroup}>
              <label htmlFor="param-min-days" className={styles.sliderLabel}>
                Mínimo de dias
              </label>
              <div className={styles.sliderRow}>
                <input
                  id="param-min-days"
                  type="range"
                  className={styles.sliderInput}
                  min={1}
                  max={30}
                  value={paramMinDays}
                  onChange={(e) => setParamMinDays(Number(e.target.value))}
                />
                <span className={styles.sliderValue} aria-live="polite">
                  {paramMinDays} {paramMinDays === 1 ? 'dia' : 'dias'}
                </span>
              </div>
            </div>
            <div className={styles.sliderGroup}>
              <label htmlFor="param-max-days" className={styles.sliderLabel}>
                Máximo de dias
              </label>
              <div className={styles.sliderRow}>
                <input
                  id="param-max-days"
                  type="range"
                  className={styles.sliderInput}
                  min={1}
                  max={90}
                  value={paramMaxDays}
                  onChange={(e) => setParamMaxDays(Number(e.target.value))}
                />
                <span className={styles.sliderValue} aria-live="polite">
                  {paramMaxDays} {paramMaxDays === 1 ? 'dia' : 'dias'}
                </span>
              </div>
            </div>
          </div>
        );

      case 'PREREQUISITE':
        return (
          <div className={styles.parametersSection}>
            <p className={styles.parametersSectionTitle}>Configurar Pré-requisito</p>
            <div className={styles.prerequisiteRow}>
              <div className={styles.prerequisiteSelect}>
                <Select
                  label="Procedimento obrigatório"
                  options={procedureSelectOptions}
                  value={paramPrerequisiteFrom}
                  onChange={(e) => setParamPrerequisiteFrom(e.target.value)}
                  placeholder="Selecione..."
                  fullWidth
                />
              </div>
              <span className={styles.prerequisiteArrow} aria-hidden="true">
                <ArrowRight size={20} />
              </span>
              <div className={styles.prerequisiteSelect}>
                <Select
                  label="Antes de"
                  options={procedureSelectOptions}
                  value={paramPrerequisiteTo}
                  onChange={(e) => setParamPrerequisiteTo(e.target.value)}
                  placeholder="Selecione..."
                  fullWidth
                />
              </div>
            </div>
          </div>
        );

      case 'MIN_INTERVAL':
        return (
          <div className={styles.parametersSection}>
            <p className={styles.parametersSectionTitle}>Intervalo Mínimo</p>
            <Input
              label="Horas mínimas entre procedimentos"
              type="number"
              value={String(paramMinHours)}
              onChange={(e) => setParamMinHours(Number(e.target.value))}
              min={1}
              fullWidth
              hint="Número mínimo de horas entre dois procedimentos."
            />
          </div>
        );

      case 'MAX_INTERVAL':
        return (
          <div className={styles.parametersSection}>
            <p className={styles.parametersSectionTitle}>Intervalo Máximo</p>
            <Input
              label="Dias máximos entre procedimentos"
              type="number"
              value={String(paramMaxIntervalDays)}
              onChange={(e) => setParamMaxIntervalDays(Number(e.target.value))}
              min={1}
              fullWidth
              hint="Número máximo de dias entre dois procedimentos consecutivos."
            />
          </div>
        );
    }
  };

  const formFields = (
    <>
      <Input
        label="Nome da regra"
        value={formName}
        onChange={(e) => setFormName(e.target.value)}
        placeholder="Ex: Janela de reagendamento padrao"
        required
        fullWidth
      />
      <div className={styles.textareaWrapper}>
        <label htmlFor="policy-description" className={styles.textareaLabel}>
          Descrição (opcional)
        </label>
        <textarea
          id="policy-description"
          className={styles.textarea}
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          rows={2}
          placeholder="Descreva a regra..."
        />
      </div>
      <Select
        label="Tipo de regra"
        options={POLICY_TYPE_OPTIONS}
        value={formPolicyType}
        onChange={(e) => setFormPolicyType(e.target.value as PolicyType)}
        fullWidth
      />
      <Select
        label="Campanha"
        options={campaignSelectOptions}
        value={formCampaignId}
        onChange={(e) => setFormCampaignId(e.target.value)}
        fullWidth
        hint="Deixe como Global para aplicar a todas as campanhas."
      />
      {renderParameterFields()}
      {formError && (
        <p className={styles.formError} role="alert">
          {formError}
        </p>
      )}
    </>
  );

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar regras. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Regras</h1>
          <p className={styles.subtitle}>
            Gerencie regras de agendamento e pré-requisitos.
          </p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
          Nova Regra
        </Button>
      </header>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <Select
            label="Filtrar por tipo"
            options={TYPE_FILTER_OPTIONS}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as PolicyType | '')}
          />
        </div>
        <div className={styles.filterGroup}>
          <Select
            label="Filtrar por status"
            options={ACTIVE_FILTER_OPTIONS}
            value={activeFilter}
            onChange={(e) =>
              setActiveFilter(e.target.value as '' | 'true' | 'false')
            }
          />
        </div>
        <div className={styles.filterGroup}>
          <Select
            label="Filtrar por campanha"
            options={campaignFilterOptions}
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando regras...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={policies}
          keyExtractor={(policy) => policy.id}
          emptyMessage="Nenhuma regra encontrada."
        />
      )}

      {/* Create modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Nova Regra"
        size="lg"
      >
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Criar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editingPolicy !== null}
        onClose={closeEditModal}
        title="Editar Regra"
        size="lg"
      >
        <form onSubmit={handleUpdate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeEditModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
